/**
 * Shared browser utility functions.
 *
 * Extracted from ciphertext.ts and hooks.ts to avoid duplication.
 * All functions include SSR guards for safe server-side rendering.
 */

/**
 * Generate a UUID v4
 */
export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Generate a unique device ID that persists across sessions.
 * Falls back to a random UUID when localStorage is unavailable (SSR).
 */
export function generateDeviceId(): string {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return generateUUID();
  }

  const storageKey = 'vesant_device_id';
  let deviceId = localStorage.getItem(storageKey);

  if (!deviceId) {
    deviceId = generateUUID();
    localStorage.setItem(storageKey, deviceId);
  }

  return deviceId;
}

/**
 * Detect browser information from user agent.
 * Returns safe defaults when running server-side.
 */
export function getBrowserInfo(): {
  browser: string;
  browser_version: string;
  os: string;
  os_version: string;
} {
  if (typeof navigator === 'undefined') {
    return { browser: 'unknown', browser_version: '', os: 'unknown', os_version: '' };
  }

  const ua = navigator.userAgent;
  let browser = 'unknown';
  let browserVersion = '';
  let os = 'unknown';
  let osVersion = '';

  // Detect browser
  if (ua.includes('Firefox/')) {
    browser = 'Firefox';
    browserVersion = ua.match(/Firefox\/([\d.]+)/)?.[1] || '';
  } else if (ua.includes('Edg/')) {
    browser = 'Edge';
    browserVersion = ua.match(/Edg\/([\d.]+)/)?.[1] || '';
  } else if (ua.includes('Chrome/')) {
    browser = 'Chrome';
    browserVersion = ua.match(/Chrome\/([\d.]+)/)?.[1] || '';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browser = 'Safari';
    browserVersion = ua.match(/Version\/([\d.]+)/)?.[1] || '';
  } else if (ua.includes('Opera') || ua.includes('OPR/')) {
    browser = 'Opera';
    browserVersion = ua.match(/(?:Opera|OPR)\/([\d.]+)/)?.[1] || '';
  }

  // Detect OS
  if (ua.includes('Windows')) {
    os = 'Windows';
    if (ua.includes('Windows NT 10.0')) osVersion = '10';
    else if (ua.includes('Windows NT 6.3')) osVersion = '8.1';
    else if (ua.includes('Windows NT 6.2')) osVersion = '8';
    else if (ua.includes('Windows NT 6.1')) osVersion = '7';
  } else if (ua.includes('Mac OS X')) {
    os = 'macOS';
    osVersion = ua.match(/Mac OS X ([\d_]+)/)?.[1]?.replace(/_/g, '.') || '';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('Android')) {
    os = 'Android';
    osVersion = ua.match(/Android ([\d.]+)/)?.[1] || '';
  } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS';
    osVersion = ua.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, '.') || '';
  }

  return { browser, browser_version: browserVersion, os, os_version: osVersion };
}
